import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';

import { RouterModule, Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { ThemeService } from '../../../services/theme.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './navbar-v2.component.html'
})
export class NavbarComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private destroy$ = new Subject<void>();
  
  mobileMenuOpen = false;
  userMenuOpen = signal(false);
  isAuthenticated = signal(false);
  userInitial = signal('');
  userEmail = signal('');
  userName = signal('');
  isDarkMode = signal(false);
  
  // Role-based signals
  isAdmin = signal(false);
  isAdvisor = signal(false);
  isLender = signal(false);

  ngOnInit() {
    // Subscribe to theme changes
    this.themeService.darkMode$.pipe(takeUntil(this.destroy$)).subscribe(isDark => {
      this.isDarkMode.set(isDark);
    });

    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.isAuthenticated.set(!!user);
      if (user) {
        this.userEmail.set(user.email || '');
        this.userInitial.set((user.email?.charAt(0) || 'U').toUpperCase());
      } else {
        this.userEmail.set('');
        this.userInitial.set('');
        // Reset roles when user logs out
        this.isAdmin.set(false);
        this.isAdvisor.set(false);
        this.isLender.set(false);
      }
    });

    this.authService.userSettings$.pipe(takeUntil(this.destroy$)).subscribe(settings => {
      if (settings) {
        const name = settings.firstName 
          ? `${settings.firstName} ${settings.lastName || ''}`.trim()
          : settings.fullName || '';
        this.userName.set(name);
        if (name) {
          this.userInitial.set(name.charAt(0).toUpperCase());
        }
      }
    });

    // Subscribe to custom claims for role-based navigation
    this.authService.customClaims$.pipe(takeUntil(this.destroy$)).subscribe(claims => {
      const roles = claims?.roles || [];
      this.isAdmin.set(roles.includes('admin'));
      this.isAdvisor.set(roles.includes('advisor'));
      this.isLender.set(roles.includes('lender'));
    });
  }

  // Computed helpers for template
  get showRequestTerms(): boolean {
    // Hide Request Terms for lenders (unless they're also admin)
    if (this.isLender() && !this.isAdmin()) {
      return false;
    }
    return true;
  }

  get showAdvisorPortal(): boolean {
    return this.isAuthenticated() && (this.isAdvisor() || this.isAdmin());
  }

  get showLenderPortal(): boolean {
    return this.isAuthenticated() && (this.isLender() || this.isAdmin());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.userMenuOpen.set(false);
  }

  toggleUserMenu() {
    this.userMenuOpen.update(v => !v);
  }

  closeUserMenu() {
    this.userMenuOpen.set(false);
  }

  scrollToSection(sectionId: string) {
    this.mobileMenuOpen = false;
    this.userMenuOpen.set(false);

    // About and Contact are standalone pages now (v3).
    if (sectionId === 'about' || sectionId === 'contact') {
      this.router.navigate(['/' + sectionId]);
      return;
    }
    
    // Check if we're on the home page
    const currentUrl = this.router.url;
    const isHomePage = currentUrl === '/' || currentUrl === '' || currentUrl.startsWith('/#');
    
    if (isHomePage) {
      // Already on home page, just scroll to section
      this.scrollToElement(sectionId);
    } else {
      // Navigate to home page first, then scroll to section
      this.router.navigate(['/']).then(() => {
        // Wait for navigation to complete and DOM to render
        setTimeout(() => {
          this.scrollToElement(sectionId);
        }, 100);
      });
    }
  }

  private scrollToElement(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  handleLogin() {
    this.router.navigate(['/auth']);
  }

  navigateToSettings() {
    this.userMenuOpen.set(false);
    this.mobileMenuOpen = false;
    this.router.navigate(['/user-settings']);
  }

  navigateToAdvisorPortal() {
    this.userMenuOpen.set(false);
    this.mobileMenuOpen = false;
    this.router.navigate(['/advisor-portal']);
  }

  navigateToLenderPortal() {
    this.userMenuOpen.set(false);
    this.mobileMenuOpen = false;
    this.router.navigate(['/lender-portal']);
  }

  async signOut() {
    this.userMenuOpen.set(false);
    this.mobileMenuOpen = false;
    await this.authService.signOut();
    this.router.navigate(['/']);
  }

  navigateToRequestTerms() {
    this.router.navigate(['/request-terms']).then(
      () => {},
      (error) => console.error('Navigation error:', error)
    );
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }
}
